import { useMemo, useState } from "react";

import {
  Button,
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";

import type {
  EnterpriseMessage,
} from "@swot/swot-components";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
} from "@components/data-grid/index.js";

import {
  CmtsPageHintPanel,
} from "@components/hints/index.js";

import {
  CmtsEnterpriseModal,
} from "@components/CmtsEnterpriseModal.js";

import {
  CmtsEnterpriseForm,
} from "@components/forms/index.js";

import {
  readCmtsSession,
} from "@cmts/auth/authStore.js";


type MessageRow =
  Record<string, unknown>;

type MessageMode =
  | "new"
  | "reply"
  | "task"
  | null;

type CmtsFormOption = {
  readonly value: string;
  readonly label: string;
};

type ActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
};

function text(
  row: Record<string, unknown>,
  keys: readonly string[],
  fallback = "",
): string {
  for (const key of keys) {
    const value = row[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim()
    ) {
      return String(value);
    }
  }

  return fallback;
}

function booleanValue(
  row: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  for (const key of keys) {
    const value = row[key];

    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      if (value.trim().toLowerCase() === "true") {
        return true;
      }

      if (value.trim().toLowerCase() === "false") {
        return false;
      }
    }
  }

  return false;
}

function messageId(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["id", "messageId", "threadId"],
  );
}

function messageStatus(
  row: Record<string, unknown>,
): string {
  if (
    booleanValue(row, ["isArchived"]) ||
    Boolean(row.archivedAt) ||
    Boolean(row.archivedAtUtc)
  ) {
    return "ARCHIVED";
  }

  if (
    booleanValue(row, ["isRead"]) ||
    Boolean(row.readAt) ||
    Boolean(row.readAtUtc)
  ) {
    return "READ";
  }

  return text(
    row,
    ["readStatus", "messageStatus", "status"],
    "UNREAD",
  )
    .trim()
    .toUpperCase();
}

function isArchived(
  row: Record<string, unknown>,
): boolean {
  return (
    messageStatus(row) === "ARCHIVED" ||
    Boolean(row.archivedAt) ||
    Boolean(row.archivedAtUtc)
  );
}

function isRead(
  row: Record<string, unknown>,
): boolean {
  return (
    Boolean(row.readAt) ||
    Boolean(row.readAtUtc) ||
    ["READ", "ACKNOWLEDGED"].includes(
      messageStatus(row),
    )
  );
}

function messageVisibility(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    [
      "visibilityCode",
      "visibility",
      "confidentiality",
    ],
    "INTERNAL_ONLY",
  )
    .trim()
    .toUpperCase();
}

function readUserIds(
  row: Record<string, unknown>,
  keys: readonly string[],
): readonly string[] {
  const values = new Set<string>();

  for (const key of keys) {
    const raw = row[key];

    if (Array.isArray(raw)) {
      raw.forEach((item) => {
        const value = String(item ?? "")
          .trim()
          .toLowerCase();

        if (value) {
          values.add(value);
        }
      });

      continue;
    }

    if (
      typeof raw === "string" &&
      raw.trim()
    ) {
      const input = raw.trim();

      if (
        input.startsWith("[") &&
        input.endsWith("]")
      ) {
        try {
          const parsed = JSON.parse(
            input,
          ) as unknown;

          if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
              const value = String(
                item ?? "",
              )
                .trim()
                .toLowerCase();

              if (value) {
                values.add(value);
              }
            });

            continue;
          }
        } catch {
          // Use comma-separated fallback.
        }
      }

      input.split(",").forEach((item) => {
        const value = item
          .trim()
          .toLowerCase();

        if (value) {
          values.add(value);
        }
      });
    }
  }

  return [...values];
}

function canCurrentUserSeeMessage(
  row: Record<string, unknown>,
  currentUserId: string,
  canViewAllMessages: boolean,
): boolean {
  if (
    canViewAllMessages ||
    !currentUserId
  ) {
    return true;
  }

  const normalizedUserId =
    currentUserId
      .trim()
      .toLowerCase();

  const senderIds = readUserIds(
    row,
    [
      "senderUserId",
      "latestSenderUserId",
      "createdByUserId",
      "createdBy",
      "authorId",
    ],
  );

  const recipientIds = readUserIds(
    row,
    [
      "recipientUserId",
      "recipientUserIds",
      "participantUserIds",
      "participantIds",
      "participants",
    ],
  );

  if (
    senderIds.length === 0 &&
    recipientIds.length === 0
  ) {
    return true;
  }

  return (
    senderIds.includes(normalizedUserId) ||
    recipientIds.includes(normalizedUserId)
  );
}

// function linkedId(
// function linkedId(
//   row: Record<string, unknown>,
// ): string {
//   return text(row, [
//     "linkedEntityId",
//     "parentComplaintId",
//     "complaintId",
//     "sourceEntityId",
//   ]);
// }
function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function resolveComplaintLabel(
  row: Record<string, unknown>,
  complaintOptions: readonly { value: string; label: string }[] = [],
): string {
  // 1. Check if the row already has a human-readable code
  const explicit = text(row, [
    "linkedRecordCode",
    "linkedRecord",
    "complaintCode",
    "complaintNumber",
    "linkedEntityCode",
    "threadCode",
  ]);

  if (
    explicit &&
    explicit !== "-" &&
    explicit.toLowerCase() !== "general" &&
    !isUuid(explicit)
  ) {
    return explicit;
  }

  // 2. Otherwise look up the raw ID in complaintOptions
  const rawId = text(row, [
    "complaintId",
    "parentComplaintId",
    "linkedEntityId",
    "sourceEntityId",
  ]);

  if (!rawId || rawId === "-" || rawId.toLowerCase() === "general") {
    const threadNum = text(row, ["threadNumber", "threadCode"], "");
    return threadNum || "-";
  }

  const clean = rawId.trim().toLowerCase();
  const match = complaintOptions.find(
    (opt) =>
      opt.value.trim().toLowerCase() === clean ||
      opt.label.trim().toLowerCase() === clean,
  );

  if (match) {
    return match.label;
  }

  return explicit && explicit !== "-" && !isUuid(explicit) ? explicit : rawId;
}

function linkedId(
  row: Record<string, unknown>,
  complaintOptions: readonly { value: string; label: string }[] = [],
): string {
  return resolveComplaintLabel(row, complaintOptions);
}

function linkedComplaintId(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    [
      "complaintId",
      "parentComplaintId",
      "linkedEntityId",
    ],
  );
}

// Resolve a raw userId to a human-readable name using the userOptions list.
// Falls back to the raw ID string if no matching option is found.
function resolveUserName(
  userId: string,
  userOptions: readonly { value: string; label: string }[],
): string {
  if (!userId || userId === "-") return "-";
  const cleanId = userId.trim().toLowerCase();
  const match = userOptions.find(
    (opt) =>
      opt.value.trim().toLowerCase() === cleanId ||
      opt.label.trim().toLowerCase() === cleanId,
  );
  return match ? match.label : userId;
}

function formatDateTime(
  row: Record<string, unknown>,
): string {
   
  const value = text(row, [
    "createdAt",
    "createdAtUtc",
    "sentAt",
  ]);

  if (!value) {
    return "-";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString();
}

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger
          ? "cmts-notification-action-option--danger"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

function buildMessageFields(
  visibilityOptions:
    readonly CmtsFormOption[] = [],
  userOptions:
    readonly CmtsFormOption[] = [],
  complaintOptions:
    readonly CmtsFormOption[] = [],
) {
  return [
    {
      name: "body",
      label: "Message",
      type: "textarea" as const,
      required: true,
      minLength: 4,
    },
    {
      name: "subject",
      label: "Subject",
      required: true,
      minLength: 3,
    },
    {
      name: "threadType",
      label: "Thread Type",
      type: "select" as const,
      required: true,
      options: [
        {
          value: "INTERNAL",
          label: "Internal",
        },
        {
          value: "CUSTOMER",
          label: "Customer",
        },
        {
          value: "SUPPLIER",
          label: "Supplier",
        },
      ],
    },
    {
      name: "visibility",
      label: "Visibility",
      type: "select" as const,
      required: true,
      options: visibilityOptions,
    },
    {
      name: "complaintId",
      label: "Linked complaint",
      type: "select" as const,
      options: [
        {
          value: "",
          label: "No linked complaint",
        },
        ...complaintOptions,
      ],
    },
    {
      name: "recipientUserId",
      label: "Recipient",
      type: "select" as const,
      required: true,
      placeholder: "Select recipient",
      options: userOptions,
    },
  ] as const;
}

function buildTaskFields(
  userOptions:
    readonly CmtsFormOption[] = [],
) {
  return [
    {
      name: "title",
      label: "Task title",
      required: true,
      minLength: 4,
    },
    {
      name: "description",
      label: "Task description",
      type: "textarea" as const,
      required: true,
      minLength: 4,
    },
    {
      name: "assignedToUserId",
      label: "Assign to",
      type: "select" as const,
      required: true,
      options: userOptions,
    },
    {
      name: "priorityCode",
      label: "Priority",
      type: "select" as const,
      required: true,
      options: [
        { value: "LOW", label: "Low" },
        { value: "MEDIUM", label: "Medium" },
        { value: "HIGH", label: "High" },
        { value: "CRITICAL", label: "Critical" },
      ],
    },
  ] as const;
}

const replyFields = [
  {
    name: "body",
    label: "Reply message",
    type: "textarea" as const,
    required: true,
    minLength: 2,
  },
] as const;

export function CmtsMessagesPage(props: {
  thread?:
    | readonly EnterpriseMessage[]
    | undefined;

  rows?:
    | readonly MessageRow[]
    | undefined;

  diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;

  busy?: boolean | undefined;
  error?: string | null | undefined;

  onCreate: (
    payload: Record<string, unknown>,
  ) => void | Promise<void>;

  onReply: (
    row: MessageRow,
    payload: Record<string, unknown>,
  ) => void | Promise<void>;

  onArchive: (
    row: MessageRow,
  ) => void | Promise<void>;

  onMarkRead: (
    row: MessageRow,
  ) => void | Promise<void>;

  onMarkUnread: (
    row: MessageRow,
  ) => void | Promise<void>;

  onCreateTask: (
    row: MessageRow,
    payload: Record<string, unknown>,
  ) => void | Promise<void>;

  onOpen: (
    row: MessageRow,
  ) => void | Promise<void>;

  onRefresh?: (() => void | Promise<void>) | undefined;

  masterOptions?: {
    readonly visibility?: readonly CmtsFormOption[];
    readonly userOptions?: readonly CmtsFormOption[];
    readonly complaintOptions?: readonly CmtsFormOption[];
  };
}) {
  const sourceRows = (
    props.rows ??
    props.thread ??
    []
  ) as readonly MessageRow[];

  const session = useMemo(
    () => readCmtsSession(),
    [],
  );

  const currentUserId = String(
    session?.userId ?? "",
  ).trim();

  const accessCodes = useMemo(
    () =>
      new Set(
        [
          ...(session?.roles ?? []),
          ...(session?.roleIds ?? []),
          ...(session?.permissions ?? []),
          ...(session?.permissionCodes ?? []),
        ].map((value) =>
          String(value)
            .trim()
            .toUpperCase(),
        ),
      ),
    [session],
  );

  const canViewAllMessages =
    accessCodes.has("PLATFORM_ADMIN") ||
    accessCodes.has("SYSTEM_ADMIN") ||
    accessCodes.has("SUPER_ADMIN") ||
    accessCodes.has("PLATFORM_ADMIN.READ") ||
    accessCodes.has("PLATFORM_ADMIN.MANAGE");

  const rows = useMemo(
    () =>
      sourceRows
        .filter((row) =>
          canCurrentUserSeeMessage(
            row,
            currentUserId,
            canViewAllMessages,
          ),
        )
        .map((row) => ({
          ...row,
          id: messageId(row),
        })),
    [
      sourceRows,
      currentUserId,
      canViewAllMessages,
    ],
  );

  const allUserOptions =
    props.masterOptions?.userOptions ?? [];

  const recipientOptions = useMemo(
    () =>
      allUserOptions.filter(
        (option) =>
          option.value
            .trim()
            .toLowerCase() !==
          currentUserId.toLowerCase(),
      ),
    [allUserOptions, currentUserId],
  );

  const messageFields =
    buildMessageFields(
      props.masterOptions?.visibility ??
        [],
      recipientOptions,
      props.masterOptions?.complaintOptions ??
        [],
    );

  const messageTaskFields =
    buildTaskFields(recipientOptions);

  const summary = useMemo(
    () => ({
      total: rows.length,
      unread: rows.filter(
        (row) => messageStatus(row) === "UNREAD",
      ).length,
      read: rows.filter(
        (row) => messageStatus(row) === "READ",
      ).length,
      archived: rows.filter(
        (row) => messageStatus(row) === "ARCHIVED",
      ).length,
    }),
    [rows],
  );

  const [mode, setMode] =
    useState<MessageMode>(null);

  const [selected, setSelected] =
    useState<MessageRow | null>(null);

  const [actionsFor, setActionsFor] =
    useState<MessageRow | null>(null);

  const openForm = (
    nextMode: Exclude<
      MessageMode,
      null
    >,
    row?: MessageRow,
  ) => {
    setSelected(row ?? null);
    setActionsFor(null);
    setMode(nextMode);
  };

  const runAction = async (
    action: () => void | Promise<void>,
  ): Promise<void> => {
    try {
      await action();
      setActionsFor(null);
    } catch {
      // Container mutation error is displayed via props.error.
    }
  };

  const columns: readonly CmtsGridColumn<MessageRow>[] =
    [
      {
        id: "subject",
        header: "Thread / Subject",
        sortable: true,
        filterable: true,
        minWidth: 230,
        accessor: (row) =>
          text(
            row,
            [
              "subject",
              "title",
              "body",
            ],
            "Message thread",
          ),
        render: (row) => (
          <div className="cmts-notification-title">
            <strong>
              {text(
                row,
                [
                  "subject",
                  "title",
                  "body",
                ],
                "Message thread",
              )}
            </strong>

            <span>
              {text(
                row,
                [
                  "threadType",
                  "visibility",
                ],
                "CMTS",
              )}
            </span>
          </div>
        ),
      },
      {
        id: "body",
        header: "Latest Message",
        sortable: true,
        filterable: true,
        minWidth: 260,
        accessor: (row) =>
          text(
            row,
            [
              "body",
              "latestMessage",
              "messageBody",
              "content",
              "lastMessage",
              "message",
              "description",
            ],
            "-",
          ),
        render: (row) =>
          text(
            row,
            [
              "body",
              "latestMessage",
              "messageBody",
              "content",
              "lastMessage",
              "message",
              "description",
            ],
            "-",
          ),
      },
      {
        id: "visibility",
        header: "Visibility",
        sortable: true,
        filterable: true,
        minWidth: 120,
        accessor: (row) =>
          messageVisibility(row),
        render: (row) =>
          messageVisibility(row),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        filterable: true,
        minWidth: 110,
        accessor: (row) =>
          messageStatus(row),
        render: (row) => (
          <span
            className={`cmts-status-chip cmts-status-chip--${messageStatus(
              row,
            ).toLowerCase()}`}
          >
            {messageStatus(row)}
          </span>
        ),
      },
      {
        id: "authorId",
        header: "Sender",
        sortable: true,
        filterable: true,
        minWidth: 150,
        accessor: (row) => {
          const explicitName = text(
            row,
            [
              "senderName",
              "createdByName",
              "authorName",
              "ownerName",
              "userName",
              "recipientName",
            ],
          );
          if (explicitName && explicitName !== "-") return explicitName;
          const rawId = text(
            row,
            [
              "senderUserId",
              "createdByUserId",
              "authorId",
              "createdBy",
              "lastMessageByUserId",
              "recipientUserId",
              "ownerUserId",
              "userId",
            ],
          );
          if (!rawId) return "-";
          return resolveUserName(
            rawId,
            allUserOptions,
          );
        },
        render: (row) => {
          // 1. First try to find an explicit pre-resolved name in the row.
          const explicitName = text(
            row,
            [
              "senderName",
              "createdByName",
              "authorName",
              "ownerName",
              "userName",
              "recipientName",
            ],
          );
          if (explicitName && explicitName !== "-") return explicitName;

          // 2. Otherwise resolve the candidate user ID (sender/creator/author/recipient)
          // to a display name using the userOptions lookup table.
          const rawId = text(
            row,
            [
              "senderUserId",
              "createdByUserId",
              "authorId",
              "createdBy",
              "lastMessageByUserId",
              "recipientUserId",
              "ownerUserId",
              "userId",
            ],
          );

          if (!rawId) return "-";

          return resolveUserName(
            rawId,
            allUserOptions,
          );
        },
      },
      {
        id: "complaintId",
        header: "Linked Record",
        sortable: true,
        filterable: true,
        minWidth: 160,
        accessor: (row) =>
          linkedId(row, props.masterOptions?.complaintOptions ?? []),
        render: (row) =>
          linkedId(row, props.masterOptions?.complaintOptions ?? []) || "-",
      },
      {
        id: "createdAt",
        header: "Created",
        sortable: true,
        filterable: true,
        minWidth: 170,
        render: formatDateTime,
      },
      {
        id: "messageActions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 125,
        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label="Open message actions"
            onClick={(event) => {
              event.stopPropagation();
              setActionsFor(row);
            }}
          >
            <span
              className="cmts-notification-actions-button__icon"
              aria-hidden="true"
            >
              ⋮
            </span>

            <span>Actions</span>
          </button>
        ),
      },
    ];

  const selectedIsArchived =
    actionsFor !== null &&
    isArchived(actionsFor);

  const selectedIsRead =
    actionsFor !== null &&
    isRead(actionsFor);

  const selectedHasLinkedRecord =
    actionsFor !== null &&
    Boolean(linkedComplaintId(actionsFor));

  return (
    <PageContainer
      title="Messages"
      subtitle="Secure thread-based communication for CMTS work."
    >
     
      <CmtsPageHintPanel defaultOpen={false}>
        Messages are repository-backed. Reply, read/unread, archive and task creation actions persist through the backend.
      </CmtsPageHintPanel>

      <section className="cmts-workspace-hero">
        <div>
          <p className="cmts-section-eyebrow">
            MY WORK · COMMUNICATION
          </p>

          <h2>Conversation centre</h2>

          <p>
            {canViewAllMessages
              ? "Platform administrator view: all tenant conversations are available."
              : "Only conversations that you sent or received are available."}
          </p>
        </div>

        <div className="cmts-page-actions">
          <Button
            intent="button"
            className="cmts-action-button"
            disabled={props.busy}
            onClick={() => openForm("new")}
          >
            + New message
          </Button>
          {props.onRefresh ? (
            <Button
              intent="button"
              className="cmts-secondary-button"
              disabled={props.busy}
              onClick={props.onRefresh}
            >
              Refresh
            </Button>
          ) : null}
        </div>
      </section>

      <section
        className="cmts-message-summary"
        aria-label="Message summary"
      >
        <article>
          <span>Total conversations</span>
          <strong>{summary.total}</strong>
        </article>

        <article className="cmts-message-summary__unread">
          <span>Unread</span>
          <strong>{summary.unread}</strong>
        </article>

        <article className="cmts-message-summary__read">
          <span>Read</span>
          <strong>{summary.read}</strong>
        </article>

        <article className="cmts-message-summary__archived">
          <span>Archived</span>
          <strong>{summary.archived}</strong>
        </article>
      </section>

      <CmtsSmartDataGrid<MessageRow>
        title="Message register"
        subtitle="Complaint, RCA, CAPA, verification, escalation and operations discussion threads."
        rows={rows}
        columns={columns}
        rowKey="id"
        defaultPageSize={10}
        exportable
        loading={props.busy}
        error={props.error}
        filters={[
          {
            id: "status",
            label: "Status",
            columnId: "status",
            operator: "contains",
            placeholder: "All statuses",
          },
          {
            id: "visibility",
            label: "Visibility",
            columnId: "visibility",
            operator: "contains",
            placeholder:
              "All visibility",
          },
          {
            id: "complaintId",
            label: "Linked record",
            columnId: "complaintId",
            operator: "contains",
            placeholder: "All records",
          },
        ]}
        emptyMessage="No message threads are available for this persona."
      />

      {/* Message actions popup */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Message actions"
        subtitle={
          actionsFor
            ? text(
                actionsFor,
                [
                  "subject",
                  "title",
                  "body",
                ],
                `Message ${messageId(
                  actionsFor,
                )}`,
              )
            : ""
        }
        size="md"
        onClose={() => {
          if (!props.busy) {
            setActionsFor(null);
          }
        }}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current status</span>
                <strong>
                  {messageStatus(
                    actionsFor,
                  )}
                </strong>
              </div>

              <div>
                <span>Visibility</span>
                <strong>
                  {messageVisibility(actionsFor)}
                </strong>
              </div>

              <div>
                <span>Created</span>
                <strong>
                  {formatDateTime(
                    actionsFor,
                  )}
                </strong>
              </div>
            </div>

            {props.error && (
              <div
                className="cmts-notification-action-modal__error"
                role="alert"
              >
                {props.error}
              </div>
            )}

            <div className="cmts-notification-action-modal__grid">
              <ActionCard
                icon="↗"
                title="Open linked record"
                description="Open the linked complaint and message workflow."
                disabled={
                  props.busy ||
                  !selectedHasLinkedRecord
                }
                onClick={() => {
                  const row = actionsFor;
                  void runAction(() =>
                    props.onOpen(row),
                  );
                }}
              />

              <ActionCard
                icon="↩"
                title="Reply"
                description="Add a reply to this message thread."
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  openForm(
                    "reply",
                    actionsFor,
                  );
                }}
              />

              <ActionCard
                icon="✓"
                title="Mark as read"
                description="Move this message to the read state."
                disabled={
                  props.busy ||
                  selectedIsArchived ||
                  selectedIsRead
                }
                onClick={() => {
                  const row = actionsFor;
                  void runAction(() =>
                    props.onMarkRead(row),
                  );
                }}
              />

              <ActionCard
                icon="○"
                title="Mark as unread"
                description="Return this message to the unread state."
                disabled={
                  props.busy ||
                  selectedIsArchived ||
                  !selectedIsRead
                }
                onClick={() => {
                  const row = actionsFor;
                  void runAction(() =>
                    props.onMarkUnread(row),
                  );
                }}
              />

              <ActionCard
                icon="+"
                title="Create task"
                description="Create a follow-up task from this message."
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  openForm(
                    "task",
                    actionsFor,
                  );
                }}
              />
            </div>

            <div className="cmts-notification-action-modal__danger">
              <ActionCard
                icon="×"
                title={
                  selectedIsArchived
                    ? "Message archived"
                    : "Archive message"
                }
                description="Remove this message from the active communication lifecycle."
                danger
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  const row = actionsFor;
                  void runAction(() =>
                    props.onArchive(row),
                  );
                }}
              />
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* New / Reply / Create task form */}
      <CmtsEnterpriseModal
        open={mode !== null}
        title={
          mode === "new"
            ? "New message"
            : mode === "reply"
              ? "Reply to thread"
              : "Create task from message"
        }
        subtitle={
          selected
            ? `Thread ${text(
                selected,
                ["threadId", "id"],
                "selected",
              )}`
            : "Create communication linked to CMTS work"
        }
        size="xl"
        onClose={() => {
          if (!props.busy) {
            setMode(null);
          }
        }}
      >
        <div className={`cmts-message-compose cmts-message-compose--${mode ?? "new"}`}>
          <header className="cmts-message-compose__hero">
            <div className="cmts-message-compose__hero-icon" aria-hidden="true">
              {mode === "task" ? "✓" : mode === "reply" ? "↩" : "✉"}
            </div>
            <div className="cmts-message-compose__hero-copy">
              <span>MY WORK · SECURE COMMUNICATION</span>
              <h3>
                {mode === "task"
                  ? "Turn this conversation into action"
                  : mode === "reply"
                    ? "Continue the conversation"
                    : "Compose a new message"}
              </h3>
              <p>
                {mode === "task"
                  ? "Assign a clear follow-up with priority and ownership."
                  : "Choose the audience, access level and linked work record before sending."}
              </p>
            </div>
            <div className="cmts-message-compose__secure">
              <span aria-hidden="true">●</span>
              Role scoped
            </div>
          </header>

          {mode === "new" ? (
            <div className="cmts-message-compose__steps" aria-label="Message creation steps">
              <div><span>1</span><strong>Write</strong><small>Message and subject</small></div>
              <div><span>2</span><strong>Classify</strong><small>Type and visibility</small></div>
              <div><span>3</span><strong>Route</strong><small>Recipient and linked work</small></div>
            </div>
          ) : null}

          <div className="cmts-message-compose__section-title">
            <span>{mode === "task" ? "TASK DETAILS" : "MESSAGE DETAILS"}</span>
            <small>Fields marked with * are required</small>
          </div>

          <CmtsEnterpriseForm
          fields={
            mode === "task"
              ? messageTaskFields
              : mode === "reply"
                ? replyFields
                : messageFields
          }
          initialValues={
            mode === "new"
              ? {}
              : mode === "reply" &&
                  selected
                ? {
                    threadId: text(
                      selected,
                      ["threadId", "id"],
                    ),
                  }
                : {
                    title: selected
                      ? `Follow up: ${text(
                          selected,
                          [
                            "subject",
                            "body",
                          ],
                          "message",
                        )}`
                      : "",
                    description: selected
                      ? text(
                          selected,
                          [
                            "latestMessage",
                            "messageBody",
                            "body",
                          ],
                        )
                      : "",
                    priorityCode: "MEDIUM",
                  }
          }
          submitLabel={
            mode === "task"
              ? "Create task"
              : mode === "reply"
                ? "Send reply"
                : "Send message"
          }
          busy={props.busy}
          error={props.error}
          onCancel={() =>
            setMode(null)
          }
          onSubmit={async (
            payload,
          ) => {
            if (mode === "new") {
              await props.onCreate(
                {
                  ...payload,
                  messageBody:
                    payload.body,
                  visibilityCode:
                    payload.visibility,
                },
              );
            } else if (
              mode === "reply" &&
              selected
            ) {
              await props.onReply(
                selected,
                {
                  ...payload,
                  messageBody:
                    payload.body,
                  messageTypeCode:
                    "REPLY",
                  isInternal:
                    !messageVisibility(
                      selected,
                    ).includes(
                      "EXTERNAL",
                    ),
                },
              );
            } else if (
              mode === "task" &&
              selected
            ) {
              await props.onCreateTask(
                selected,
                {
                  ...payload,
                  complaintId:
                    linkedComplaintId(
                      selected,
                    ),
                  linkedEntityId:
                    linkedComplaintId(
                      selected,
                    ),
                  parentComplaintId:
                    linkedComplaintId(
                      selected,
                    ),
                  ownerUserId:
                    payload.assignedToUserId,
                  taskTypeCode:
                    "MESSAGE_FOLLOW_UP",
                },
              );
            }

            setMode(null);
          }}
          />
        </div>
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}